import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { BrandLockup } from "../components/BrandLockup";
import { GlassModal } from "../components/GlassModal";
import {
  applyMintToLedger,
  readSessionKitLedger,
  writeSessionKitLedger,
  type SessionKitLedger,
} from "../data/mint-kit-session-ledger";
import { CHECKPOINT_MATERIAL_MEDIA_NOTE } from "../data/checkpoint-material";
import { renderQrSvg } from "../lib/qr-svg";
import {
  buildMintRequest,
  buildSingleCheckpointMintRequest,
  executeMintRequest,
  executeSingleCheckpointMintRequest,
  type MintedToken,
} from "../data/token-minter";

export type RouteMintConfirmTarget = {
  kind: "route";
  routeId: string;
  routeName: string;
  replace: boolean;
  hasActiveMaterial: boolean;
};

export type CheckpointMintConfirmTarget = {
  kind: "checkpoint";
  checkpointId: string;
  checkpointName: string;
  routeLabel: string;
  hasExistingMaterial: boolean;
  replace: boolean;
  /**
   * PRD-001I unrouted mint: set when the caller could not read this checkpoint's material
   * counts (Wave A2 added management_checkpoint_material_status, but the console deploys
   * ahead of migrations and the read fails soft). The confirm then says the state could not
   * be read instead of claiming "active" or "none", and the server stays the arbiter: a plain
   * mint is refused when material exists; replace works whether or not it does.
   */
  materialUnknown?: boolean;
};

export type MintConfirmTarget = RouteMintConfirmTarget | CheckpointMintConfirmTarget;

export interface TokenMintSessionContextValue {
  enabled: boolean;
  mintGeneration: number;
  openRouteConfirm: (target: Omit<RouteMintConfirmTarget, "kind">) => void;
  openCheckpointConfirm: (target: Omit<CheckpointMintConfirmTarget, "kind">) => void;
  hasSessionKit: (checkpointId: string) => boolean;
  hasRouteSessionKit: (routeId: string) => boolean;
  openSessionKit: (checkpointId: string) => void;
  openRouteSessionKit: (routeId: string) => void;
}

const TokenMintSessionContext = createContext<TokenMintSessionContextValue | null>(null);

const MINT_KIT_OUTPUT_NOTE =
  "This kit can be printed or exported as QR labels and/or written to NFC tags with an external writer; the console does not write blank NFC tags in the browser.";

function CheckpointQR({ value, humanCode }: { value: string; humanCode: string }) {
  const svg = useMemo(() => renderQrSvg(value), [value]);
  return (
    <div
      className="mintkit__qr"
      role="img"
      aria-label={humanCode ? `QR code for ${humanCode}` : "Checkpoint QR code"}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

function MintKitRow({ row }: { row: MintedToken }) {
  return (
    <li className="mintkit__row">
      <div className="mintkit__meta">
        <span className="mintkit__order">#{row.routeOrder}</span>
        <span className="mintkit__name">{row.checkpointName}</span>
        {row.humanCode ? <span className="mintkit__code">{row.humanCode}</span> : null}
      </div>
      <p className="mintkit__media-note">{CHECKPOINT_MATERIAL_MEDIA_NOTE}</p>
      <CheckpointQR value={row.token} humanCode={row.humanCode} />
      <p className="mintkit__nfc">
        <span className="mintkit__nfc-label">External NFC write value</span>
        <code className="mintkit__nfc-value">{row.token}</code>
      </p>
    </li>
  );
}

export function useTokenMintSession(): TokenMintSessionContextValue | null {
  return useContext(TokenMintSessionContext);
}

function confirmTitle(target: MintConfirmTarget): string {
  if (target.kind === "route") {
    return target.replace ? `Confirm replace — ${target.routeName}` : `Confirm mint — ${target.routeName}`;
  }
  return target.replace
    ? `Confirm replace — ${target.checkpointName}`
    : `Confirm mint — ${target.checkpointName}`;
}

function confirmPrimaryLabel(target: MintConfirmTarget): string {
  return target.replace ? "Confirm replace" : "Confirm mint";
}

function kitAriaLabel(target: MintConfirmTarget | null): string {
  if (target === null) return "One-time checkpoint kit";
  if (target.kind === "route") return `One-time kit — ${target.routeName}`;
  return `One-time checkpoint kit — ${target.checkpointName}`;
}

function confirmBlocked(target: MintConfirmTarget): boolean {
  if (target.replace) return false;
  if (target.kind === "route") return target.hasActiveMaterial;
  return target.hasExistingMaterial;
}

export function TokenMintSessionProvider({
  client,
  accountId,
  enabled,
  children,
}: {
  client: SupabaseClient;
  accountId: string;
  enabled: boolean;
  children: ReactNode;
}) {
  const [confirmTarget, setConfirmTarget] = useState<MintConfirmTarget | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [kit, setKit] = useState<MintedToken[] | null>(null);
  const [kitTarget, setKitTarget] = useState<MintConfirmTarget | null>(null);
  const [mintGeneration, setMintGeneration] = useState(0);
  const [ledger, setLedger] = useState<SessionKitLedger>(() => readSessionKitLedger(accountId));

  useEffect(() => {
    setLedger(readSessionKitLedger(accountId));
  }, [accountId]);

  const closeConfirm = useCallback(() => {
    if (busy) return;
    setConfirmTarget(null);
    setFailure(null);
  }, [busy]);

  const closeKit = useCallback(() => {
    setKit(null);
    setKitTarget(null);
  }, []);

  const openRouteConfirm = useCallback(
    (target: Omit<RouteMintConfirmTarget, "kind">) => {
      if (!enabled) return;
      setFailure(null);
      setConfirmTarget({ kind: "route", ...target });
    },
    [enabled],
  );

  const openCheckpointConfirm = useCallback(
    (target: Omit<CheckpointMintConfirmTarget, "kind">) => {
      if (!enabled) return;
      setFailure(null);
      setConfirmTarget({ kind: "checkpoint", ...target });
    },
    [enabled],
  );

  const hasSessionKit = useCallback(
    (checkpointId: string) => ledger.byCheckpoint[checkpointId] !== undefined,
    [ledger],
  );

  const hasRouteSessionKit = useCallback(
    (routeId: string) => (ledger.routeKits[routeId]?.tokens.length ?? 0) > 0,
    [ledger],
  );

  const openSessionKit = useCallback(
    (checkpointId: string) => {
      const token = ledger.byCheckpoint[checkpointId];
      if (token === undefined) return;
      setKit([token]);
      setKitTarget({
        kind: "checkpoint",
        checkpointId,
        checkpointName: token.checkpointName,
        routeLabel: "",
        hasExistingMaterial: true,
        replace: false,
      });
    },
    [ledger],
  );

  const openRouteSessionKit = useCallback(
    (routeId: string) => {
      const entry = ledger.routeKits[routeId];
      if (entry === undefined || entry.tokens.length === 0) return;
      setKit(entry.tokens);
      setKitTarget({
        kind: "route",
        routeId,
        routeName: entry.routeName,
        replace: false,
        hasActiveMaterial: true,
      });
    },
    [ledger],
  );

  const confirmMint = useCallback(async () => {
    if (confirmTarget === null || busy || confirmBlocked(confirmTarget)) return;
    setBusy(true);
    setFailure(null);
    const outcome =
      confirmTarget.kind === "route"
        ? await executeMintRequest(
            client,
            buildMintRequest(accountId, confirmTarget.routeId, {
              tokenType: "QR",
              replaceExisting: confirmTarget.replace,
            }),
          )
        : await executeSingleCheckpointMintRequest(
            client,
            buildSingleCheckpointMintRequest(accountId, confirmTarget.checkpointId, {
              tokenType: "QR",
              replaceExisting: confirmTarget.replace,
            }),
          );
    setBusy(false);
    if (!outcome.ok) {
      setFailure(outcome.reason);
      return;
    }
    const routeKit =
      confirmTarget.kind === "route"
        ? { routeId: confirmTarget.routeId, routeName: confirmTarget.routeName }
        : undefined;
    setLedger((previous) => {
      const next = applyMintToLedger(previous, outcome.tokens, routeKit);
      writeSessionKitLedger(accountId, next);
      return next;
    });
    setKitTarget(confirmTarget);
    setKit(outcome.tokens);
    setConfirmTarget(null);
    setMintGeneration((value) => value + 1);
  }, [accountId, busy, client, confirmTarget]);

  const contextValue = useMemo<TokenMintSessionContextValue>(
    () => ({
      enabled,
      mintGeneration,
      openRouteConfirm,
      openCheckpointConfirm,
      hasSessionKit,
      hasRouteSessionKit,
      openSessionKit,
      openRouteSessionKit,
    }),
    [
      enabled,
      mintGeneration,
      openRouteConfirm,
      openCheckpointConfirm,
      hasSessionKit,
      hasRouteSessionKit,
      openSessionKit,
      openRouteSessionKit,
    ],
  );

  const blocked = confirmTarget !== null && confirmBlocked(confirmTarget);

  return (
    <TokenMintSessionContext.Provider value={contextValue}>
      {children}

      {enabled && confirmTarget !== null ? (
        <GlassModal
          open
          onClose={closeConfirm}
          ariaLabel={confirmTitle(confirmTarget)}
          busy={busy}
          size="md"
        >
          <div className="minter__confirm">
            <h3 className="minter__confirm-title">{confirmTitle(confirmTarget)}</h3>
            {confirmTarget.kind === "route" ? (
              <p className="minter__confirm-warning">
                {confirmTarget.replace
                  ? "This retires active checkpoint material on every stop in this route. Mounted tags stop working immediately."
                  : "This mints one checkpoint secret per active stop."}
              </p>
            ) : (
              <p className="minter__confirm-warning">
                {confirmTarget.replace
                  ? confirmTarget.materialUnknown === true
                    ? "If this checkpoint already has active material, it is retired and mounted tags stop working immediately. If it has none yet, this simply mints its first secret."
                    : "This retires the checkpoint's active material. Old QR labels and NFC tags stop working immediately."
                  : confirmTarget.materialUnknown === true
                    ? "This creates one checkpoint secret. If active material already exists the server refuses this mint; use Replace material instead."
                    : "This creates one checkpoint secret."}
              </p>
            )}
            <p className="minter__confirm-note">{MINT_KIT_OUTPUT_NOTE}</p>
            {confirmTarget.kind === "checkpoint" ? (
              <p className="minter__confirm-note">
                {confirmTarget.routeLabel || "Checkpoint"} ·{" "}
                {confirmTarget.materialUnknown === true
                  ? "Material state could not be read for this checkpoint"
                  : confirmTarget.hasExistingMaterial
                    ? "Checkpoint material active"
                    : "No checkpoint material yet"}
              </p>
            ) : null}
            <p className="minter__confirm-note">{CHECKPOINT_MATERIAL_MEDIA_NOTE}</p>
            {blocked ? (
              <p className="surface__state surface__state--error" role="alert">
                Active checkpoint material already exists. Close this dialog and use{" "}
                <strong>Replace tokens</strong> or <strong>Replace material</strong> instead.
              </p>
            ) : null}
            {failure !== null ? (
              <p className="surface__state surface__state--error" role="alert">
                {failure}
              </p>
            ) : null}
            <div className="minter__confirm-actions">
              <button type="button" className="builder__navbtn" disabled={busy} onClick={closeConfirm}>
                Cancel
              </button>
              <button
                type="button"
                className="minter__confirm-go"
                disabled={busy || blocked}
                onClick={() => void confirmMint()}
              >
                {busy ? "Minting…" : confirmPrimaryLabel(confirmTarget)}
              </button>
            </div>
          </div>
        </GlassModal>
      ) : null}

      {enabled && kit !== null ? (
        <GlassModal open onClose={closeKit} ariaLabel={kitAriaLabel(kitTarget)} size="lg">
          <section className="mintkit" aria-label={kitAriaLabel(kitTarget)}>
            <header className="mintkit__head">
              <BrandLockup
                link={false}
                className="mintkit__brand brandlockup"
                markClassName="brandlockup__mark mintkit__brand-mark"
                textClassName="brandlockup__text mintkit__brand-text"
              />
              <h3 className="mintkit__title">Checkpoint kit</h3>
              <p className="mintkit__warn">{MINT_KIT_OUTPUT_NOTE}</p>
              <p className="minter__confirm-note">
                This tab keeps kits you mint here until you close the browser tab. Use View kit to
                reopen them in this tab.
              </p>
            </header>
            <ol className="mintkit__list">
              {kit.map((row) => (
                <MintKitRow
                  key={`${row.checkpointId}-${row.routeOrder}-${row.humanCode}`}
                  row={row}
                />
              ))}
            </ol>
            <footer className="mintkit__foot">
              <button type="button" className="builder__navbtn" onClick={closeKit}>
                Close kit
              </button>
            </footer>
          </section>
        </GlassModal>
      ) : null}
    </TokenMintSessionContext.Provider>
  );
}
